import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Trash2, Check, Eye, Archive, RefreshCw, ShoppingCart, Users, DollarSign, X, Palette, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatINR } from '../utils/currency';

const BACKEND_URL = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('orders');
  const [contacts, setContacts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [angels, setAngels] = useState([]);
  const [artSubmissions, setArtSubmissions] = useState([]);
  const [brandSubmissions, setBrandSubmissions] = useState([]);
  const [selectedAngel, setSelectedAngel] = useState(null);
  const [angelOrders, setAngelOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const url = filter === 'all' 
        ? `${BACKEND_URL}/api/admin/contacts`
        : `${BACKEND_URL}/api/admin/contacts?status=${filter}`;
      
      const { data } = await axios.get(url, { withCredentials: true });
      setContacts(data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/orders`, { withCredentials: true });
      setOrders(data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAngels = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/angels`, { withCredentials: true });
      setAngels(data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching angels:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAngelOrders = useCallback(async (angelId) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/angels/${angelId}/orders`, { withCredentials: true });
      setAngelOrders(data.orders);
      setSelectedAngel(data.angel);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching angel orders:', error);
    }
  }, []);

  const fetchArtSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const url = filter === 'all' 
        ? `${BACKEND_URL}/api/admin/art-submissions`
        : `${BACKEND_URL}/api/admin/art-submissions?status=${filter}`;
      
      const { data } = await axios.get(url, { withCredentials: true });
      setArtSubmissions(data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching art submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchBrandSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const url = filter === 'all'
        ? `${BACKEND_URL}/api/admin/brand-submissions`
        : `${BACKEND_URL}/api/admin/brand-submissions?status=${filter}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setBrandSubmissions(data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching brand submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    
    if (activeTab === 'contacts') {
      fetchContacts();
    } else if (activeTab === 'orders') {
      fetchOrders();
    } else if (activeTab === 'angels') {
      fetchAngels();
    } else if (activeTab === 'art-submissions') {
      fetchArtSubmissions();
    } else if (activeTab === 'brand-submissions') {
      fetchBrandSubmissions();
    }
  }, [user, navigate, activeTab, fetchContacts, fetchOrders, fetchAngels, fetchArtSubmissions, fetchBrandSubmissions]);

  const updateContactStatus = async (id, status) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/contacts/${id}/status?status=${status}`,
        {},
        { withCredentials: true }
      );
      fetchContacts();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error updating status:', error);
    }
  };

  const updateFulfillment = async (orderId, step, value) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/orders/${orderId}/fulfillment`,
        { step, value },
        { withCredentials: true }
      );
      fetchOrders();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Fulfillment update failed:', error);
      alert('Failed to update step: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const [sharingTrinity, setSharingTrinity] = useState(null);
  const shareToTrinity = async (orderId) => {
    try {
      setSharingTrinity(orderId);
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/orders/${orderId}/share-trinity`,
        {},
        { withCredentials: true }
      );
      const r = res.data?.recipients || {};
      let msg = '✅ Invoice shared with the trinity:\n\n';
      msg += `• Customer: ${r.customer || '-'}\n`;
      msg += `• Brand admin: ${r.brand_admin || '-'}\n`;
      msg += `• Influencer: ${r.influencer || '-'}`;
      if (res.data?.test_mode) msg += '\n\n(Test mode — actually delivered to admin only.)';
      alert(msg);
      fetchOrders();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('share-trinity failed:', error);
      alert('Failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSharingTrinity(null);
    }
  };

  const sendInvoice = async (orderId) => {
    if (!window.confirm('Send invoice email to customer, angel, and admin (CC)?')) {
      return;
    }
    
    try {
      setSendingInvoice(orderId);
      await axios.post(
        `${BACKEND_URL}/api/admin/orders/${orderId}/send-invoice`,
        {},
        { withCredentials: true }
      );
      alert('Invoice sent successfully! ✅');
      fetchOrders();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error sending invoice:', error);
      alert('Failed to send invoice: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSendingInvoice(null);
    }
  };

  const updateArtSubmissionStatus = async (id, status) => {
    const confirmMessage = status === 'approved' 
      ? 'Approve this art submission? It will be marked as accepted.'
      : 'Delete this art submission? This action cannot be undone.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      if (status === 'deleted') {
        await axios.delete(
          `${BACKEND_URL}/api/admin/art-submissions/${id}`,
          { withCredentials: true }
        );
        alert('Art submission deleted successfully!');
      } else {
        await axios.put(
          `${BACKEND_URL}/api/admin/art-submissions/${id}/status?status=${status}`,
          {},
          { withCredentials: true }
        );
        alert(`Art submission ${status} successfully!`);
      }
      fetchArtSubmissions();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error updating art submission:', error);
      alert('Failed to update art submission: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const syncArtSubmissionsToProducts = async () => {
    if (!window.confirm('Sync all approved art submissions to products? This will create missing products and fix images.')) {
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/art-submissions/sync-products`,
        {},
        { withCredentials: true }
      );
      
      const { created, updated, skipped, errors } = response.data;
      let message = `Sync completed!\n\nCreated: ${created}\nUpdated: ${updated}\nSkipped: ${skipped}`;
      
      if (errors && errors.length > 0) {
        message += `\n\nErrors:\n${errors.join('\n')}`;
      }
      
      alert(message);
      fetchArtSubmissions();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error syncing art submissions:', error);
      alert('Failed to sync art submissions: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const syncMmcFurniture = async () => {
    if (!window.confirm('Fetch the latest catalogue from mmcwoodcreations.com and sync into Furniture products?')) {
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/sync-mmc-products`,
        {},
        { withCredentials: true }
      );

      const { created, updated, skipped, total_fetched, errors } = response.data;
      let message = `MMC Furniture sync completed!\n\nFetched: ${total_fetched}\nCreated: ${created}\nUpdated: ${updated}\nSkipped: ${skipped}`;

      if (errors && errors.length > 0) {
        message += `\n\nErrors (${errors.length}):\n${errors.slice(0, 5).join('\n')}`;
      }

      alert(message);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error syncing MMC products:', error);
      alert('Failed to sync MMC products: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const syncOvercmr = async () => {
    if (!window.confirm('Fetch the latest catalogue from overcmr.com and sync into Health & Fitness products?')) {
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/sync-overcmr`,
        {},
        { withCredentials: true }
      );

      const { created, updated, skipped, total_fetched, errors } = response.data;
      let message = `Overcmr sync completed!\n\nFetched: ${total_fetched}\nCreated: ${created}\nUpdated: ${updated}\nSkipped: ${skipped}`;

      if (errors && errors.length > 0) {
        message += `\n\nErrors (${errors.length}):\n${errors.slice(0, 5).join('\n')}`;
      }

      alert(message);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error syncing Overcmr products:', error);
      alert('Failed to sync Overcmr products: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const updateBrandSubmissionStatus = async (id, status) => {
    const prompts = {
      approved: 'Approve this brand? Its products will go live under Featured Brands.',
      rejected: 'Reject this brand? Any previously published products will be removed.',
      deleted: 'Delete this brand submission and all its products? This cannot be undone.'
    };
    if (!window.confirm(prompts[status] || 'Update status?')) return;

    try {
      if (status === 'deleted') {
        await axios.delete(`${BACKEND_URL}/api/admin/brand-submissions/${id}`, { withCredentials: true });
        alert('Brand submission deleted.');
      } else {
        const res = await axios.put(
          `${BACKEND_URL}/api/admin/brand-submissions/${id}/status?status=${status}`,
          {},
          { withCredentials: true }
        );
        const created = res.data?.products_created ?? 0;
        alert(status === 'approved'
          ? `Brand approved! ${created} product(s) published under Featured Brands.`
          : `Brand ${status}.`);
      }
      fetchBrandSubmissions();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error updating brand submission:', error);
      alert('Failed: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      unread: 'bg-blue-100 text-blue-800',
      read: 'bg-gray-100 text-gray-800',
      replied: 'bg-green-100 text-green-800',
      archived: 'bg-purple-100 text-purple-800',
      pending_payment: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      pending: 'bg-orange-100 text-orange-800',
      earned: 'bg-green-100 text-green-800',
      paid: 'bg-blue-100 text-blue-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading && activeTab !== 'angels') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
              <p className="text-gray-600">Manage orders, angels, and customer inquiries</p>
            </div>
            <button
              onClick={syncMmcFurniture}
              className="px-4 py-2 bg-amber-700 text-white rounded-lg hover:bg-amber-800 transition text-sm font-medium flex items-center"
              data-testid="sync-mmc-furniture-btn"
              title="Fetch latest furniture catalogue from mmcwoodcreations.com"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync MMC Furniture
            </button>
            <button
              onClick={syncOvercmr}
              className="px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition text-sm font-medium flex items-center"
              data-testid="sync-overcmr-btn"
              title="Fetch latest fitness & wellness catalogue from overcmr.com"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync Overcmr
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'orders'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ShoppingCart className="inline w-5 h-5 mr-2" />
                Orders Management
              </button>
              <button
                onClick={() => setActiveTab('angels')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'angels'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Users className="inline w-5 h-5 mr-2" />
                Nanaki's Kaurs
              </button>
              <button
                onClick={() => setActiveTab('contacts')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'contacts'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Mail className="inline w-5 h-5 mr-2" />
                Contact Forms
              </button>
              <button
                onClick={() => setActiveTab('art-submissions')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'art-submissions'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Palette className="inline w-5 h-5 mr-2" />
                Art Submissions
              </button>
              <button
                onClick={() => setActiveTab('brand-submissions')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'brand-submissions'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid="admin-tab-brand-submissions"
              >
                <Sparkles className="inline w-5 h-5 mr-2" />
                Brand Submissions
              </button>
            </nav>
          </div>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Orders Management</h2>
                <p className="text-sm text-gray-600 mt-1">Track all orders with customer details and payment status</p>
              </div>
              <button
                onClick={fetchOrders}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
            
            <div className="overflow-x-auto">
              {orders.length === 0 ? (
                <div className="p-12 text-center text-gray-600">
                  <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p>No orders yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200" data-testid="orders-list">
                  {orders.map((order) => {
                    const f = order.fulfillment || { order_collected: false, payment_made: false, delivered: false };
                    const steps = [
                      { key: 'order_collected', label: 'Order collected' },
                      { key: 'payment_made', label: 'Payment made' },
                      { key: 'delivered', label: 'Order reached customer' }
                    ];
                    return (
                      <div key={order.id} className="p-5" data-testid={`order-card-${order.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-mono text-sm font-semibold text-purple-700">#{order.order_number}</span>
                            <span className="text-sm text-gray-500">
                              {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusBadge(order.order_status)}`}>
                              {order.order_status}
                            </span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-lg text-[#B6594C]">{formatINR(order.final_total)}</div>
                            {order.discount_amount > 0 && (
                              <div className="text-xs text-green-700">discount: -{formatINR(order.discount_amount)}</div>
                            )}
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 mb-4 text-sm">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Customer</p>
                            <div className="font-semibold">{order.customer_name}</div>
                            <div className="text-gray-600">{order.customer_email}</div>
                            <div className="text-gray-500">{order.customer_mobile}</div>
                            <div className="text-xs text-gray-400 mt-1">ID: {order.customer_id}</div>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Delivery address</p>
                            <p className="text-gray-700 whitespace-pre-line">
                              {order.delivery_address ? (() => {
                                const a = order.delivery_address;
                                const fullName = a.full_name || [a.firstName, a.lastName].filter(Boolean).join(' ').trim();
                                const line1 = a.address_line1 || a.address;
                                const line2 = a.address_line2 || a.apartment;
                                const pin = a.pincode || a.pinCode;
                                return (
                                  <>
                                    {fullName && <span className="block">{fullName}</span>}
                                    {line1 && <span className="block">{line1}</span>}
                                    {line2 && <span className="block">{line2}</span>}
                                    <span className="block">
                                      {[a.city, a.state, pin].filter(Boolean).join(', ')}
                                    </span>
                                    {a.phone && <span className="block">{a.phone}</span>}
                                  </>
                                );
                              })() : (
                                <span className="text-gray-400">— no address on file —</span>
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Influencer / Kaur</p>
                            {order.angel_name ? (
                              <>
                                <div className="font-semibold">{order.angel_name}</div>
                                {order.discount_code && <div className="text-xs text-gray-500">code: {order.discount_code}</div>}
                                {order.commission_amount > 0 && (
                                  <div className="text-xs text-green-700 mt-0.5">
                                    Commission: {formatINR(order.commission_amount)}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-400">— no referral —</span>
                            )}
                            <div className="text-xs text-gray-500 mt-1">{order.items_count} item(s)</div>
                          </div>
                        </div>

                        {/* Fulfillment progress */}
                        <div className="bg-gray-50 rounded-lg p-4 mb-3">
                          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">Fulfillment progress</p>
                          <div className="grid sm:grid-cols-3 gap-3">
                            {steps.map((s, i) => {
                              const done = !!f[s.key];
                              const prevDone = i === 0 ? true : !!f[steps[i - 1].key];
                              return (
                                <button
                                  key={s.key}
                                  type="button"
                                  onClick={() => updateFulfillment(order.id, s.key, !done)}
                                  disabled={!done && !prevDone}
                                  data-testid={`fulfillment-${s.key}-${order.id}`}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm transition text-left ${
                                    done
                                      ? 'bg-green-50 border-green-400 text-green-800'
                                      : prevDone
                                      ? 'bg-white border-gray-300 hover:border-purple-400 text-gray-700'
                                      : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                  }`}
                                >
                                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                    done ? 'bg-green-600 border-green-600' : 'border-gray-400'
                                  }`}>
                                    {done && <Check className="w-3 h-3 text-white" />}
                                  </span>
                                  <span className="font-semibold">{i + 1}. {s.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 justify-end items-center">
                          {order.trinity_shared_at && (
                            <span className="text-xs text-green-700 mr-auto">
                              ✓ Trinity shared {new Date(order.trinity_shared_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                          <button
                            onClick={() => shareToTrinity(order.id)}
                            disabled={sharingTrinity === order.id}
                            data-testid={`share-trinity-${order.id}`}
                            className="px-4 py-2 bg-gradient-to-r from-[#B6594C] to-[#9d4a40] text-white text-sm font-semibold rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            <Mail className="w-4 h-4" />
                            {sharingTrinity === order.id ? 'Sharing…' : 'Share to Trinity'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Kaurs Tab */}
        {activeTab === 'angels' && (
          <div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <div className="p-6 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Nanaki's Kaurs Tracking</h2>
                  <p className="text-sm text-gray-600 mt-1">Monitor angel performance and commissions</p>
                </div>
                <button
                  onClick={fetchAngels}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </button>
              </div>
              
              <div className="overflow-x-auto">
                {angels.length === 0 ? (
                  <div className="p-12 text-center text-gray-600">
                    <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p>No angels registered yet</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kaur ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referral Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referrals</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Earnings</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Earned</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {angels.map((angel) => (
                        <tr key={angel.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-mono text-sm bg-purple-100 text-purple-800 px-2 py-1 rounded">
                              {angel.angel_id}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-semibold text-gray-900">{angel.name}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-600">{angel.email}</div>
                            <div className="text-sm text-gray-500">{angel.mobile}</div>
                          </td>
                          <td className="px-6 py-4">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">{angel.referral_code}</code>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-lg font-bold text-blue-600">{angel.total_referrals}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-bold text-green-600">{formatINR(angel.total_earnings)}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-orange-600">{formatINR(angel.pending_earnings)}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-green-600">{formatINR(angel.earned_commissions)}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => fetchAngelOrders(angel.id)}
                              className="text-purple-600 hover:text-purple-900 font-semibold text-sm"
                            >
                              View Orders
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Kaur Orders Modal */}
            {selectedAngel && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                  <div className="p-6 border-b flex justify-between items-center">
                    <div>
                      <h3 className="text-2xl font-bold">Orders for {selectedAngel.name}</h3>
                      <p className="text-sm text-gray-600">Referral Code: {selectedAngel.referral_code}</p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedAngel(null);
                        setAngelOrders([]);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="overflow-y-auto max-h-[70vh]">
                    {angelOrders.length === 0 ? (
                      <div className="p-12 text-center text-gray-600">No orders yet</div>
                    ) : (
                      <table className="w-full">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {angelOrders.map((order) => (
                            <tr key={order.order_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="font-mono text-sm font-semibold">#{order.order_number}</span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm">
                                  <div className="font-semibold">{order.customer_name}</div>
                                  <div className="text-gray-600">{order.customer_email}</div>
                                  <div className="text-gray-500">{order.customer_mobile}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                                  {order.customer_id}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold">{formatINR(order.final_total)}</div>
                                {order.discount_amount > 0 && (
                                  <div className="text-xs text-green-600">-{formatINR(order.discount_amount)}</div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="font-bold text-purple-600">{formatINR(order.commission_amount)}</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(order.commission_status)}`}>
                                  {order.commission_status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {new Date(order.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Contact Forms Tab */}
        {activeTab === 'contacts' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">Contact Form Submissions</h2>
            </div>
            
            <div className="p-4 border-b">
              <div className="flex space-x-2">
                {['all', 'unread', 'read', 'replied', 'archived'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      filter === status
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="divide-y">
              {contacts.length === 0 ? (
                <div className="p-12 text-center text-gray-600">
                  <Mail className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p>No contact submissions</p>
                </div>
              ) : (
                contacts.map((contact) => (
                  <div key={contact.id} className="p-6 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">{contact.name}</h3>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(contact.status)}`}>
                            {contact.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{contact.email}</p>
                        <p className="text-gray-700">{contact.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(contact.submitted_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => updateContactStatus(contact.id, 'read')}
                          className="p-2 text-gray-600 hover:text-gray-900"
                          title="Mark as read"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => updateContactStatus(contact.id, 'replied')}
                          className="p-2 text-green-600 hover:text-green-900"
                          title="Mark as replied"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => updateContactStatus(contact.id, 'archived')}
                          className="p-2 text-purple-600 hover:text-purple-900"
                          title="Archive"
                        >
                          <Archive className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Art Submissions Tab */}
        {activeTab === 'art-submissions' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Art Submissions</h2>
                <p className="text-gray-600 mt-1">Review and manage artist submissions</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={syncArtSubmissionsToProducts}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                  title="Sync approved artwork to products"
                >
                  Sync to Products
                </button>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                >
                  <option value="all">All Submissions</option>
                  <option value="pending">Pending Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <button
                  onClick={fetchArtSubmissions}
                  className="p-2 text-purple-600 hover:text-purple-900"
                  title="Refresh"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {artSubmissions.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <Palette className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg">No art submissions found</p>
                </div>
              ) : (
                artSubmissions.map((submission) => (
                  <div key={submission.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {submission.art_title}
                          </h3>
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(submission.status)}`}>
                            {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-sm text-gray-600">
                              <strong>Artist:</strong> {submission.artist_name}
                            </p>
                            <p className="text-sm text-gray-600">
                              <strong>Email:</strong>{' '}
                              <a href={`mailto:${submission.email}`} className="text-purple-600 hover:underline">
                                {submission.email}
                              </a>
                            </p>
                            <p className="text-sm text-gray-600">
                              <strong>Phone:</strong> {submission.phone}
                            </p>
                            {submission.instagram_handle && (
                              <p className="text-sm text-gray-600">
                                <strong>Instagram:</strong>{' '}
                                <a
                                  href={`https://instagram.com/${submission.instagram_handle.replace('@', '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:underline"
                                >
                                  {submission.instagram_handle}
                                </a>
                              </p>
                            )}
                          </div>

                          <div>
                            <p className="text-sm text-gray-600">
                              <strong>Medium:</strong> {submission.art_medium.charAt(0).toUpperCase() + submission.art_medium.slice(1)}
                            </p>
                            <p className="text-sm text-gray-600">
                              <strong>Submitted:</strong>{' '}
                              {new Date(submission.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            {submission.portfolio_link && (
                              <p className="text-sm text-gray-600">
                                <strong>Portfolio:</strong>{' '}
                                <a
                                  href={submission.portfolio_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:underline flex items-center"
                                >
                                  View Portfolio
                                  <Eye className="w-3 h-3 ml-1" />
                                </a>
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="mb-3">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Description:</p>
                          <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                            {submission.art_description}
                          </p>
                        </div>

                        {/* Artwork Files */}
                        {submission.artwork_files && submission.artwork_files.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-gray-700 mb-2">
                              Artwork Files ({submission.artwork_files.length}):
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {submission.artwork_files.map((file, index) => (
                                <div
                                  key={index}
                                  className="border border-gray-200 rounded-lg p-2 hover:border-purple-500 transition"
                                >
                                  {file.data_url ? (
                                    <div>
                                      <img 
                                        src={file.data_url} 
                                        alt={file.original_name}
                                        className="w-full h-32 object-cover rounded mb-2"
                                      />
                                      <p className="text-xs font-medium text-gray-700 truncate">
                                        {file.original_name}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="flex items-center space-x-2">
                                      <Eye className="w-4 h-4 text-purple-600" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-gray-700 truncate">
                                          {file.original_name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {(file.size / 1024 / 1024).toFixed(2)} MB
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {submission.message && (
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-gray-700 mb-1">Additional Message:</p>
                            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                              {submission.message}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col space-y-2 ml-4">
                        {submission.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateArtSubmissionStatus(submission.id, 'approved')}
                              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                              title="Approve submission"
                            >
                              <Check className="w-4 h-4" />
                              <span>Approve</span>
                            </button>
                            <button
                              onClick={() => updateArtSubmissionStatus(submission.id, 'rejected')}
                              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                              title="Reject submission"
                            >
                              <X className="w-4 h-4" />
                              <span>Reject</span>
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => updateArtSubmissionStatus(submission.id, 'deleted')}
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                          title="Delete submission"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Brand Submissions Tab */}
        {activeTab === 'brand-submissions' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="brand-submissions-panel">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Brand Submissions</h2>
                <p className="text-gray-600 mt-1">Approve budding women-led brands to appear under Featured Brands</p>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                >
                  <option value="all">All Submissions</option>
                  <option value="pending">Pending Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <button
                  onClick={fetchBrandSubmissions}
                  className="p-2 text-purple-600 hover:text-purple-900"
                  title="Refresh"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {brandSubmissions.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <Sparkles className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg">No brand submissions found</p>
                </div>
              ) : (
                brandSubmissions.map((submission) => (
                  <div key={submission.id} className="p-6 hover:bg-gray-50" data-testid={`brand-submission-${submission.id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-3 flex-wrap">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {submission.brand_name}
                          </h3>
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(submission.status)}`}>
                            {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                          </span>
                          {submission.wants_managed_service && (
                            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-pink-100 text-pink-800">
                              Wants ₹2,323/mo managed service
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-sm text-gray-600">
                              <strong>Founder:</strong> {submission.founder_name}
                            </p>
                            <p className="text-sm text-gray-600">
                              <strong>Email:</strong>{' '}
                              <a href={`mailto:${submission.email}`} className="text-purple-600 hover:underline">
                                {submission.email}
                              </a>
                            </p>
                            <p className="text-sm text-gray-600">
                              <strong>Phone:</strong> {submission.phone}
                            </p>
                            {submission.city && (
                              <p className="text-sm text-gray-600">
                                <strong>City:</strong> {submission.city}
                              </p>
                            )}
                          </div>
                          <div>
                            {submission.instagram_handle && (
                              <p className="text-sm text-gray-600">
                                <strong>Instagram:</strong>{' '}
                                <a
                                  href={`https://instagram.com/${submission.instagram_handle.replace('@', '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:underline"
                                >
                                  {submission.instagram_handle}
                                </a>
                              </p>
                            )}
                            {submission.website_url && (
                              <p className="text-sm text-gray-600">
                                <strong>Website:</strong>{' '}
                                <a
                                  href={submission.website_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:underline"
                                >
                                  {submission.website_url}
                                </a>
                              </p>
                            )}
                            <p className="text-sm text-gray-600">
                              <strong>Submitted:</strong>{' '}
                              {new Date(submission.created_at).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'long', day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>

                        <div className="mb-3">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Brand story:</p>
                          <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded whitespace-pre-line">
                            {submission.brand_description}
                          </p>
                        </div>

                        {Array.isArray(submission.products) && submission.products.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-gray-700 mb-2">
                              Products ({submission.products.length}):
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                              {submission.products.map((p, pi) => (
                                <div key={pi} className="border border-gray-200 rounded-lg p-2 bg-white">
                                  {p.image_data_url && (
                                    <img
                                      src={p.image_data_url}
                                      alt={p.name}
                                      className="w-full h-32 object-cover rounded mb-2"
                                    />
                                  )}
                                  <p className="text-sm font-semibold truncate">{p.name}</p>
                                  <p className="text-sm text-purple-700 font-bold">
                                    ₹{Number(p.price).toLocaleString('en-IN')}
                                  </p>
                                  {p.product_url && (
                                    <a
                                      href={p.product_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-purple-600 hover:underline flex items-center mt-1"
                                    >
                                      Visit <Eye className="w-3 h-3 ml-1" />
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {submission.message && (
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-gray-700 mb-1">Additional message:</p>
                            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded whitespace-pre-line">
                              {submission.message}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col space-y-2 ml-0 md:ml-4">
                        {submission.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateBrandSubmissionStatus(submission.id, 'approved')}
                              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                              data-testid={`approve-brand-${submission.id}`}
                            >
                              <Check className="w-4 h-4" />
                              <span>Approve</span>
                            </button>
                            <button
                              onClick={() => updateBrandSubmissionStatus(submission.id, 'rejected')}
                              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                              data-testid={`reject-brand-${submission.id}`}
                            >
                              <X className="w-4 h-4" />
                              <span>Reject</span>
                            </button>
                          </>
                        )}
                        {submission.status === 'approved' && (
                          <button
                            onClick={() => updateBrandSubmissionStatus(submission.id, 'rejected')}
                            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                            title="Unpublish / mark rejected"
                          >
                            <X className="w-4 h-4" />
                            <span>Unpublish</span>
                          </button>
                        )}
                        <button
                          onClick={() => updateBrandSubmissionStatus(submission.id, 'deleted')}
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                          data-testid={`delete-brand-${submission.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
